import * as Sentry from "@sentry/nuxt";
import { defineNuxtPlugin, useRuntimeConfig } from "#app";
import {
  createClockOffset,
  createLatencyLog,
  legDuration,
  type SpanAttributes,
  type SyncSpanOptions,
  type SyncTelemetry,
} from "~/utils/syncTelemetry";

/**
 * The Sentry-backed side of the annotation sync measurements.
 *
 * The only file that imports the browser SDK for this, on purpose: the
 * composable being measured takes `$syncTelemetry` from the app and falls back
 * to the no-op, so its unit tests — which provide nothing — never pull Sentry
 * into their module graph. Same shape as $reportError. @see utils/syncTelemetry
 */

/**
 * Whether this tab is in a measurement run.
 *
 * A live session produces several flushes a second per active participant, so
 * tracing every one of them is not something to leave on. During a run it has
 * to be all of them: a p95 over twenty scripted actions cannot be sampled at
 * one in ten. The switch is localStorage rather than a build flag so a tester
 * can turn it on in the browser they already have open.
 */
const isMeasuring = (environment: string): boolean => {
  try {
    if (window.localStorage.getItem("annotationSyncTracing") === "1") return true;
  } catch {
    // Storage disabled by the browser. Not a reason to fail a page load.
  }

  return environment !== "production";
};

const isPromise = (value: unknown): value is Promise<unknown> =>
  typeof (value as any)?.then === "function";

export default defineNuxtPlugin(() => {
  const environment = String(useRuntimeConfig().public.sentry?.environment ?? "");
  const clock = createClockOffset();
  const log = createLatencyLog(clock);
  const measuring = isMeasuring(environment);

  // Which tab a row came from. The reduction joins a sender's rows to a
  // receiver's, and three browsers of the same session are otherwise
  // indistinguishable in the exported CSVs.
  const clientId = Math.random().toString(36).slice(2, 10);

  const record = (op: string, startedAtMs: number, attributes: SpanAttributes = {}) => {
    const duration = legDuration(startedAtMs, Date.now());
    // Null means the clock moved rather than the work taking that long. Sentry
    // keeps its span either way; the table this fills in must not.
    if (duration === null) return;

    const { role, ...rest } = attributes;

    log.push({
      role: String(role ?? "sender"),
      leg: op,
      durationMs: duration,
      localMs: startedAtMs,
      attributes: { ...rest, client_id: clientId },
    });
  };

  /**
   * Marks every root span this creates, so the sampler can recognise one.
   *
   * The sampling context is built from a span's attributes alone and the op is
   * applied after the decision, so a sampler keyed on the op would quietly
   * never match and take one propagation in ten. @see sentry.client.config.ts
   */
  const attributesOf = (options: SyncSpanOptions): SpanAttributes => ({
    ...options.attributes,
    "annotation.sync": true,
  });

  const withTrace = <T>(options: SyncSpanOptions, run: () => T): T => {
    // The sender's trace, arriving in the broadcast. Continued rather than
    // started so the whole propagation is one trace across the three machines.
    if (options.trace) {
      return Sentry.continueTrace(
        { sentryTrace: options.trace.sentryTrace, baggage: options.trace.baggage },
        run,
      );
    }

    // A flush belongs to the change that caused it, not to whatever the page
    // was last doing: inheriting a navigation's trace would also inherit its
    // sampling decision.
    return options.newTrace ? Sentry.startNewTrace(run) : run();
  };

  const telemetry: SyncTelemetry = {
    span<T>(options: SyncSpanOptions, work: () => T): T {
      const startedAt = options.startTime ?? Date.now();

      if (!measuring) {
        const result = work();
        if (isPromise(result)) {
          return result.finally(() => record(options.op, startedAt, options.attributes)) as T;
        }
        record(options.op, startedAt, options.attributes);

        return result;
      }

      return withTrace(options, () =>
        Sentry.startSpan(
          {
            name: options.name ?? options.op,
            op: options.op,
            attributes: attributesOf(options),
            startTime: options.startTime,
          },
          () => {
            const result = work();

            if (isPromise(result)) {
              return result.finally(() =>
                record(options.op, startedAt, options.attributes),
              ) as T;
            }

            record(options.op, startedAt, options.attributes);

            return result;
          },
        ),
      );
    },

    leg(op: string, startedAtMs: number, attributes: SpanAttributes = {}) {
      if (measuring && legDuration(startedAtMs, Date.now()) !== null) {
        // Backdated: the wait is over by the time anything here hears about it,
        // and the queue already knew when it began.
        Sentry.startInactiveSpan({
          name: op,
          op,
          startTime: startedAtMs,
          attributes: { ...attributes, "annotation.sync": true },
          onlyIfParent: true,
        }).end();
      }

      record(op, startedAtMs, attributes);
    },

    legFromServer(op: string, serverStartedAtMs: number, attributes: SpanAttributes = {}) {
      const startedAtMs = clock.toLocal(serverStartedAtMs);
      // No answer from the API has been timed on this tab yet, so there is no
      // offset to read the server's stamp with. Reporting it against this
      // machine's raw clock would record the drift between two laptops as
      // latency, which is the mistake this whole path exists to avoid.
      if (startedAtMs === null) return;

      this.leg(op, startedAtMs, attributes);
    },

    observeServerClock(requestStartedAtMs: number, serverTimeMs: number) {
      clock.observe(requestStartedAtMs, serverTimeMs);
    },

    now: () => Date.now(),

    serverNow: () => clock.toServer(Date.now()) ?? Date.now(),
  };

  if (measuring) {
    // How the numbers leave the browser: `copy(__annotationSyncMetrics.csv())`
    // in each participant's console at the end of a run.
    (window as any).__annotationSyncMetrics = {
      ...log,
      clientId,
      clockOffsetMs: clock.offsetMs,
      clockUncertaintyMs: clock.uncertaintyMs,
    };
  }

  return { provide: { syncTelemetry: telemetry } };
});
